import React, { useEffect, useState } from "react";
import { UserPlus, Shield, Trash2, Loader2, Mail } from "lucide-react";
import { logins } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export default function AddLogins() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("viewer");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await logins.list();
      setItems(data.items || []);
    } catch (_) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!email.trim() || !email.includes("@")) { toast.error("Enter a valid email"); return; }
    setBusy(true);
    try {
      await logins.add(email.trim().toLowerCase(), name.trim(), role);
      toast.success("Login authorized", { description: `${name.trim() || email.trim()} can now sign in.` });
      setEmail(""); setName(""); setRole("viewer");
      load();
    } catch (e) {
      toast.error("Couldn't add login", { description: e?.response?.data?.detail || "Try again" });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (em) => {
    try {
      await logins.remove(em);
      setItems((x) => x.filter((l) => l.email !== em));
      toast.success("Login removed");
    } catch (e) {
      toast.error("Couldn't remove", { description: e?.response?.data?.detail || "" });
    }
  };

  return (
    <div data-testid="add-logins-page">
      <div className="mb-6">
        <h1 className="flex items-center gap-2 font-display text-3xl font-semibold text-white sm:text-4xl">
          <UserPlus className="text-[#00E5FF]" size={30} /> Add Logins
        </h1>
        <p className="mt-1 text-sm text-slate-400">Authorize who can sign in to your Headquarters. The name you enter is how associates greet each person.</p>
      </div>

      {/* Add form */}
      <div className="mb-6 max-w-3xl rounded-2xl border border-white/8 bg-[#050D1C]/40 p-5">
        <div className="grid gap-3 sm:grid-cols-[1.4fr_1fr_auto_auto]">
          <div className="relative">
            <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Google email" className="h-11 w-full rounded-xl border border-white/10 bg-[#030A16]/60 pl-9 pr-3 text-sm text-white placeholder:text-slate-500 focus:border-[#00E5FF]/50" data-testid="login-email-input" />
          </div>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="First name (for greetings)" className="h-11 rounded-xl border border-white/10 bg-[#030A16]/60 px-3 text-sm text-white placeholder:text-slate-500 focus:border-[#00E5FF]/50" data-testid="login-name-input" />
          <select value={role} onChange={(e) => setRole(e.target.value)} className="h-11 rounded-xl border border-white/10 bg-[#030A16]/60 px-3 text-sm text-white focus:border-[#00E5FF]/50" data-testid="login-role-select">
            <option value="viewer">Viewer</option>
            <option value="admin">Admin</option>
          </select>
          <button onClick={add} disabled={busy} className="h-11 rounded-xl bg-[#00E5FF] px-5 text-sm font-semibold text-[#030A16] transition-transform hover:scale-[1.02] disabled:opacity-50" data-testid="login-add-btn">
            {busy ? <Loader2 className="mx-auto animate-spin" size={16} /> : "Authorize"}
          </button>
        </div>
      </div>

      {/* List */}
      <div className="max-w-3xl space-y-2" data-testid="logins-list">
        {loading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-slate-500"><Loader2 className="animate-spin" size={15} /> Loading logins…</div>
        ) : (
          items.map((l) => {
            const isYou = l.email === user?.email;
            const isOwner = l.role === "admin" && l.email.includes("iq.ai.solutions");
            return (
              <div key={l.email} className="flex items-center gap-3 rounded-xl border border-white/8 bg-[#030A16]/40 p-3.5" data-testid={`login-row-${l.email}`}>
                <span className="grid h-9 w-9 place-items-center rounded-full bg-gradient-to-br from-[#00E5FF] to-[#8A2BE2] text-xs font-semibold text-[#030A16]">
                  {(l.name || l.email || "?").trim().charAt(0).toUpperCase()}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium text-white">{l.name || <span className="italic text-slate-500">no name — will greet by Google name</span>}</span>
                    {isYou && <span className="rounded-full bg-white/10 px-1.5 py-0.5 text-[10px] text-slate-300">You</span>}
                  </div>
                  <div className="truncate text-[11px] text-slate-400">{l.email}</div>
                </div>
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-mono uppercase ${l.role === "admin" ? "bg-[#00E5FF]/15 text-[#00E5FF]" : "bg-white/10 text-slate-300"}`}>
                  <Shield size={10} /> {l.role}
                </span>
                <button
                  onClick={() => remove(l.email)}
                  disabled={isOwner || isYou}
                  className="rounded-lg p-2 text-slate-500 transition-colors hover:bg-[#FF0055]/10 hover:text-[#FF3B5C] disabled:cursor-not-allowed disabled:opacity-30"
                  aria-label="Remove login"
                  data-testid={`login-remove-${l.email}`}
                >
                  <Trash2 size={15} />
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
